import {
  EMPTY_ARRAY,
  MarketTypeEnum,
  useCallbackRef,
  useMarketAccountsContext,
  useMarketsContext,
  type MarketDataCardUnifiedLiquidityMarket,
  type Security,
  type UnifiedLiquidityEnum,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useMarketSelectorBuyingPowerEnrichment } from '../MarketSelector/enrichments/useMarketSelectorBuyingPowerEnrichment';
import { useUnsettledEnrichment } from '../MarketSelector/enrichments/useUnsettledEnrichment';
import { useMarketSelectorAggregationPresets } from '../MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorValue } from '../MarketSelector/types';
import { useMarketSelector, type UseMarketSelectorParams } from '../MarketSelector/useMarketSelector';
import { marketSelectorItemsToOldItems } from '../MarketSelector/utils/items';
import { useAllowMultipleAcctsPerCred } from '../MarketSelector/utils/useAllowMultipleAcctsPerCred';
import { useMarketDataCardMarketSelectorItems } from './useMarketDataCardMarketSelectorItems';

export interface PersistMarketDataCardMarketSelection {
  selectedAggregation: string | undefined;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  unifiedLiquidityMarketSymbols?: MarketDataCardUnifiedLiquidityMarket[];
  unifiedLiquidity?: UnifiedLiquidityEnum;
}

interface MarketSelectorDarkSelectionGuards {
  getOnlyDarkOrNoDarkSelections: (
    latestSelectionIsDark: boolean,
    selections: { markets: string[]; marketAccounts: string[] }
  ) => { markets: string[]; marketAccounts: string[] };
  isLatestSelectionDark: (selections: string[]) => boolean;
}

interface PersistMarketSelectorValueFromSelectionsParams {
  value: MarketSelectorValue;
  marketsByName: ReadonlyMap<string, unknown>;
  marketAccountsByName: ReadonlyMap<string, unknown>;
  onPersistMarketSelection: (update: PersistMarketDataCardMarketSelection) => void;
}

export function persistMarketSelectorValueFromSelections({
  value,
  marketsByName,
  marketAccountsByName,
  getOnlyDarkOrNoDarkSelections,
  isLatestSelectionDark,
  onPersistMarketSelection,
}: PersistMarketSelectorValueFromSelectionsParams & MarketSelectorDarkSelectionGuards) {
  const selections = value.selections;
  const markets = selections.filter(mktOrAccName => marketsByName.has(mktOrAccName));
  const marketAccounts = selections.filter(mktOrAccName => marketAccountsByName.has(mktOrAccName));

  const allDarkOrNoDarkNewSelections = getOnlyDarkOrNoDarkSelections(isLatestSelectionDark(selections), {
    markets,
    marketAccounts,
  });

  onPersistMarketSelection({
    selectedAggregation: value.type === 'preset' ? value.presetID : undefined,
    selectedMarkets: allDarkOrNoDarkNewSelections.markets,
    selectedMarketAccounts: allDarkOrNoDarkNewSelections.marketAccounts,
  });
}

interface UseMarketDataCardMarketSelectorParams {
  security: Security | undefined;
  ulEnabled: boolean;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  selectedAggregation: string | undefined;
  onPersistMarketSelection: (update: PersistMarketDataCardMarketSelection) => void;
}

/**
 * Full Market Selector API for the market data card quick-access panel.
 * Mount only after the user opens the selector so per-symbol inventory is not built for every card up front.
 */
export function useMarketDataCardMarketSelector({
  security,
  ulEnabled,
  selectedMarkets,
  selectedMarketAccounts,
  selectedAggregation,
  onPersistMarketSelection,
}: UseMarketDataCardMarketSelectorParams) {
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { getOnlyDarkOrNoDarkSelections, isLatestSelectionDark } = useMarketSelectorDarkSelectionGuards();

  const marketSelectorItems = useMarketDataCardMarketSelectorItems({
    symbol: security?.Symbol,
    includeUnifiedLiquidityMarkets: ulEnabled,
    // This hook assumes that it is mounted conditionally, so we are happy to always make these requests.
    queryMSSRecords: true,
  });

  const oldMarketSelectorItems = useMemo(() => {
    return marketSelectorItemsToOldItems({ items: marketSelectorItems, includeInactiveMarketAccounts: true });
  }, [marketSelectorItems]);

  const newMarketSelectorValue: MarketSelectorValue = useMemo(() => {
    const selections = [...selectedMarkets, ...selectedMarketAccounts];
    return selectedAggregation
      ? { type: 'preset', presetID: selectedAggregation, selections }
      : { type: 'custom', selections };
  }, [selectedMarketAccounts, selectedMarkets, selectedAggregation]);

  const { aggregationPresets } = useMarketSelectorAggregationPresets();

  const handleNewMarketSelectorChange = useCallbackRef((value: MarketSelectorValue) => {
    persistMarketSelectorValueFromSelections({
      value,
      marketsByName,
      marketAccountsByName,
      getOnlyDarkOrNoDarkSelections,
      isLatestSelectionDark,
      onPersistMarketSelection,
    });
  });

  // This hook assumes that it is mounted conditionally, so we are happy to always make these requests.
  const unsettledEnrichment = useUnsettledEnrichment({
    security,
    marketSelectorItems,
  });

  const buyingPowerEnrichment = useMarketSelectorBuyingPowerEnrichment({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    quantity: undefined,
    tag: 'MarketDataCardMarketSelector',
    side: undefined,
    simpleModeVisibleColIDs: EMPTY_ARRAY,
  });

  const enrichmentSpecs = useMemo(
    () => compact([unsettledEnrichment, buyingPowerEnrichment]),
    [unsettledEnrichment, buyingPowerEnrichment]
  );

  const allowMultipleAcctsPerCred = useAllowMultipleAcctsPerCred({
    symbol: security?.Symbol,
    ulEnabled,
  });
  const marketSelectorOptions: UseMarketSelectorParams['options'] = useMemo(
    () => ({ allowMultipleAcctsPerCred }),
    [allowMultipleAcctsPerCred]
  );

  const marketSelector = useMarketSelector({
    value: newMarketSelectorValue,
    onChange: handleNewMarketSelectorChange,
    items: marketSelectorItems,
    presets: aggregationPresets,
    enrichmentSpecs,
    options: marketSelectorOptions,
  });

  return {
    marketSelector,
    oldMarketSelectorItems,
    marketSelectorItems,
  };
}

export function useMarketSelectorDarkSelectionGuards() {
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();

  const isMarketDark = useCallback(
    (marketName: string | undefined) => {
      const market = marketName ? marketsByName.get(marketName) : undefined;
      return market ? market.Type === MarketTypeEnum.Dark : false;
    },
    [marketsByName]
  );

  const isMarketAccountDark = useCallback(
    (marketAccountName: string | undefined) => {
      const market = marketAccountName ? marketAccountsByName.get(marketAccountName)?.Market : undefined;
      return market ? isMarketDark(market) : false;
    },
    [marketAccountsByName, isMarketDark]
  );

  const getOnlyDarkOrNoDarkSelections = useCallback(
    (latestSelectionIsDark: boolean, selections: { markets: string[]; marketAccounts: string[] }) => {
      return {
        marketAccounts: selections.marketAccounts.filter(ma =>
          latestSelectionIsDark ? isMarketAccountDark(ma) : !isMarketAccountDark(ma)
        ),
        markets: selections.markets.filter(m => (latestSelectionIsDark ? isMarketDark(m) : !isMarketDark(m))),
      };
    },
    [isMarketAccountDark, isMarketDark]
  );

  const isLatestSelectionDark = useCallback(
    (selections: string[]) => {
      const latestSelection = selections.at(-1);
      if (!latestSelection) {
        return false;
      }

      const isMarketName = marketsByName.has(latestSelection);
      return isMarketName ? isMarketDark(latestSelection) : isMarketAccountDark(latestSelection);
    },
    [marketsByName, isMarketDark, isMarketAccountDark]
  );

  return {
    isMarketDark,
    isMarketAccountDark,
    getOnlyDarkOrNoDarkSelections,
    isLatestSelectionDark,
  };
}
